import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ChevronUp, ChevronDown, Eye } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConditionSetEditor } from '@/components/conditions/ConditionSetEditor';
import type { ConditionCatalog, ConditionSetsResponse } from '@/components/conditions/types';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/settings/visibility-rules` (v0.4 章⑤ F-4、D-11/D-99/D-107/D-112/D-118)。
 * 関係ベースの可視性ルール: 情報クラス × 条件 (閲覧者との関係を含む) × 表示レベル。
 * 「Aさんから見たBさんの表示」プレビューを必ず併設する (§5-10「設定の妥当性は試さないと分からない」)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §5-4 / §5-10
 *
 * ⚠️ 休暇管理 (TD-26) 段階⑧ (platform-reviewer Major指摘): `visibility_rules.display_level` は
 * usage_point (result_kind) ごとに全く異なる語彙を持つ (compensation=none/status/hours/amount、
 * leave_visibility=absence_only/leave_only/type/full、family_info_visibility=none/exists_only/full)。
 * 情報クラスは「どの usage_point に属するか」でグループ化し、表示レベルの選択肢・ラベル・
 * 条件セットの取得先 (usage_point_id) をグループ単位で切り替える (routes/compensation-settings.ts の
 * assertDisplayLevelAllowedForInfoClass/resolveUsagePointIdForInfoClass と対になる設計)。
 */

type VisibilityGroup = {
  usagePointId: string;
  infoClasses: Array<{ value: string; label: string }>;
  displayLevels: Array<{ value: string; label: string }>;
};

const VISIBILITY_GROUPS: VisibilityGroup[] = [
  {
    usagePointId: 'visibility_rule',
    infoClasses: [
      { value: 'compensation_amount', label: '給与・単価の金額' },
      { value: 'annual_income_remaining', label: '年収の壁の残枠' },
      { value: 'evaluation_score', label: '評価スコア' },
      { value: 'sensitive_personal', label: '要配慮個人情報 (生年月日・妊産婦区分等)' },
      { value: 'attendance_reason', label: '勤怠の理由 (欠勤・遅刻の理由等)' },
      { value: 'agreement_36_representative', label: '36協定 過半数代表者の氏名' },
    ],
    displayLevels: [
      { value: 'none', label: '非表示' }, { value: 'status', label: '有無のみ表示' },
      { value: 'hours', label: '時間・状況まで表示' }, { value: 'amount', label: '金額まで表示' },
    ],
  },
  {
    usagePointId: 'leave_visibility',
    infoClasses: [{ value: 'leave_visibility', label: '休暇の情報 (種別・理由・対象家族)' }],
    displayLevels: [
      { value: 'absence_only', label: '不在の事実のみ' }, { value: 'leave_only', label: '休暇であることまで' },
      { value: 'type', label: '休暇の種別まで' }, { value: 'full', label: '理由・対象家族まで' },
    ],
  },
  {
    usagePointId: 'family_info_visibility',
    infoClasses: [{ value: 'family_info_visibility', label: '家族情報' }],
    displayLevels: [
      { value: 'none', label: '非表示' }, { value: 'exists_only', label: '件数・続柄のみ' }, { value: 'full', label: '詳細まで' },
    ],
  },
  {
    // v0.4 使い勝手フィードバック⑤ (D-U7): スキル・人材像・役職情報。/talent/skills (配下メンバーの
    // スキル確認) の表示レベルをここで設定する。既定 (ルール未設定) は fail-safe='none'。
    usagePointId: 'skill_visibility',
    infoClasses: [{ value: 'employee_skill', label: 'スキル・人材像・役職情報' }],
    displayLevels: [
      { value: 'none', label: '非表示' }, { value: 'exists_only', label: '人材像・件数のみ' }, { value: 'full', label: 'スキルの内訳まで' },
    ],
  },
];

const INFO_CLASS_LABELS: Record<string, string> = Object.fromEntries(
  VISIBILITY_GROUPS.flatMap((g) => g.infoClasses.map((ic) => [ic.value, ic.label])),
);
const GROUP_BY_INFO_CLASS: Record<string, VisibilityGroup> = Object.fromEntries(
  VISIBILITY_GROUPS.flatMap((g) => g.infoClasses.map((ic) => [ic.value, g])),
);
const resolveGroup = (infoClass: string): VisibilityGroup => GROUP_BY_INFO_CLASS[infoClass] ?? VISIBILITY_GROUPS[0]!;
const displayLevelLabel = (infoClass: string, value: string): string =>
  resolveGroup(infoClass).displayLevels.find((d) => d.value === value)?.label ?? value;

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

type Employee = { employee_id: string; employee_number: string; family_name: string; given_name: string; status: string };

function NodeSelector({ nodeId, onChange, label = '対象ノード', allowNull, nullLabel }: {
  nodeId: string | null; onChange: (id: string | null) => void; label?: string; allowNull?: boolean; nullLabel?: string;
}) {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const NULL_VALUE = '__tenant__';
  return (
    <div className="min-w-0 w-full sm:w-72">
      <label className="block text-xs text-text-sub mb-1">{label}</label>
      <Select value={nodeId ?? (allowNull ? NULL_VALUE : undefined)} onValueChange={(v) => onChange(v === NULL_VALUE ? null : v)}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="ノードを選ぶ" /></SelectTrigger>
        <SelectContent>
          {allowNull && <SelectItem value={NULL_VALUE}>{nullLabel ?? 'テナント全体の既定'}</SelectItem>}
          {flatNodes.map(({ node, depth }) => (
            <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function EmployeeSelector({ employeeId, onChange, label, allowNull, nullLabel }: {
  employeeId: string | null; onChange: (id: string | null) => void; label: string; allowNull?: boolean; nullLabel?: string;
}) {
  const { data: employees = [] } = useQuery<Employee[]>({ queryKey: ['employees'], queryFn: () => apiFetch<Employee[]>('/employees') });
  const NULL_VALUE = '__none__';
  return (
    <div className="min-w-0 w-full sm:w-72">
      <label className="block text-xs text-text-sub mb-1">{label}</label>
      <Select value={employeeId ?? (allowNull ? NULL_VALUE : undefined)} onValueChange={(v) => onChange(v === NULL_VALUE ? null : v)}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="社員を選ぶ" /></SelectTrigger>
        <SelectContent>
          {allowNull && <SelectItem value={NULL_VALUE}>{nullLabel ?? '(指定なし)'}</SelectItem>}
          {employees.map((e) => (
            <SelectItem key={e.employee_id} value={e.employee_id}>{e.family_name} {e.given_name} ({e.employee_number})</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ルール CRUD
// -----------------------------------------------------------------------------
type Rule = {
  rule_id: string; node_id: string | null; info_class: string; condition_set_id: string;
  display_level: string; sort_order: number; label: string | null;
};

type RuleDraft = {
  rule_id: string | null; node_id: string | null; info_class: string; condition_set_id: string | null;
  display_level: string; label: string;
};
const emptyRuleDraft = (infoClass: string): RuleDraft => ({
  rule_id: null, node_id: null, info_class: infoClass, condition_set_id: null,
  display_level: resolveGroup(infoClass).displayLevels[0]!.value, label: '',
});

function RuleModal({ draft, setDraft, group, conditionSets, nextSortOrder, onClose, onSaved }: {
  draft: RuleDraft; setDraft: (d: RuleDraft) => void; group: VisibilityGroup;
  conditionSets: { set_id: string; label: string | null }[]; nextSortOrder: number;
  onClose: () => void; onSaved: () => void;
}) {
  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        node_id: draft.node_id, info_class: draft.info_class, condition_set_id: draft.condition_set_id,
        display_level: draft.display_level, sort_order: draft.rule_id ? undefined : nextSortOrder,
        label: draft.label.trim() || null,
      };
      return draft.rule_id
        ? apiFetch(`/settings/visibility-rules/${draft.rule_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/settings/visibility-rules', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { onSaved(); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const canSave = !!draft.condition_set_id;

  return (
    <Modal open title={draft.rule_id ? '可視性ルールを編集' : '可視性ルールを追加'} onClose={onClose} size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">情報クラス</label>
          {/* ⚠️ 段階⑧: usage_point (result_kind) が異なるグループを跨いで選ばせると、選択済みの
              条件セット・表示レベルが別ラダーの値のまま不整合になる。同一グループ内のみ選択可にする。 */}
          <Select value={draft.info_class} onValueChange={(v) => setDraft({ ...draft, info_class: v })}>
            <SelectTrigger aria-label="情報クラス"><SelectValue /></SelectTrigger>
            <SelectContent>{group.infoClasses.map(({ value, label }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <NodeSelector nodeId={draft.node_id} onChange={(id) => setDraft({ ...draft, node_id: id })} label="適用範囲" allowNull nullLabel="テナント全体の既定" />
        <div>
          <label className="block text-sm text-text-sub mb-1">対象者条件 (閲覧者との関係を含む)</label>
          <Select value={draft.condition_set_id ?? undefined} onValueChange={(v) => setDraft({ ...draft, condition_set_id: v })}>
            <SelectTrigger aria-label="対象者条件"><SelectValue placeholder="条件セットを選ぶ" /></SelectTrigger>
            <SelectContent>
              {conditionSets.map((s) => <SelectItem key={s.set_id} value={s.set_id}>{s.label ?? s.set_id}</SelectItem>)}
            </SelectContent>
          </Select>
          {conditionSets.length === 0 && (
            <p className="text-xs text-alert-text mt-1">条件セットがまだありません。この画面下部の「対象者条件を管理」から先に作成してください。</p>
          )}
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">表示レベル</label>
          <div className="flex rounded-full border border-surface-border overflow-hidden w-fit flex-wrap" role="group" aria-label="表示レベル">
            {group.displayLevels.map(({ value, label }) => (
              <button key={value} type="button" aria-pressed={draft.display_level === value} onClick={() => setDraft({ ...draft, display_level: value })}
                className={`px-3 min-h-[44px] text-xs font-medium ${draft.display_level === value ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">ラベル (任意)</label>
          <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={100} className={inputCls} />
        </div>
      </div>
    </Modal>
  );
}

function RulesListForInfoClass({ infoClass, group, rules, canManage, conditionSets, onChanged }: {
  infoClass: string; group: VisibilityGroup; rules: Rule[]; canManage: boolean;
  conditionSets: { set_id: string; label: string | null }[]; onChanged: () => void;
}) {
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Rule | null>(null);
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const nodeNameById = useMemo(() => new Map(flattenNodes(nodesData?.roots ?? []).map(({ node }) => [node.node_id, node.name])), [nodesData]);
  const setLabel = (id: string) => conditionSets.find((s) => s.set_id === id)?.label ?? id;

  const deleteMut = useMutation({
    mutationFn: (ruleId: string) => apiFetch(`/settings/visibility-rules/${ruleId}`, { method: 'DELETE' }),
    onSuccess: () => { onChanged(); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  // ⚠️ design-and-code HIGH指摘: visibility_rules は (tenant_id, node_id, info_class, sort_order)
  // に部分UNIQUE制約を持つ (migration 0051)。2件を Promise.all で並列PUTすると、SCD-2 (close+insert)
  // の性質上どちらが先に走っても「まだ閉じていない旧行と同じ sort_order」を新行が狙って
  // 一意制約違反になりうる (condition_sets の sort_order には制約が無くこのパターンが安全だった
  // ため、そこから直接流用したのが誤りだった)。一時退避値を経由する3段階の逐次PUTにし、
  // どの時点でも重複が発生しないようにする。
  // ⚠️ 一時退避値には sort_order.min(0) を満たさない -1 ではなく、現在表示中の全ルール中の最大値+1
  // (どの行の sort_order とも衝突しない) を使う (F-2〜F-5 staging実機検証で -1 が常に400になる
  // 実装ミスと判明、CompensationSettingsPage.tsx の standard_rate_tiers と同型)。
  const move = async (rule: Rule, direction: -1 | 1) => {
    const sorted = [...rules].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex((r) => r.rule_id === rule.rule_id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    const target = sorted[targetIdx]!;
    const tempSortOrder = Math.max(...sorted.map((r) => r.sort_order), 0) + 1;
    try {
      await apiFetch(`/settings/visibility-rules/${target.rule_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: tempSortOrder }) });
      await apiFetch(`/settings/visibility-rules/${rule.rule_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: target.sort_order }) });
      await apiFetch(`/settings/visibility-rules/${target.rule_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: rule.sort_order }) });
      onChanged();
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '並び替えに失敗しました');
    }
  };

  const sorted = [...rules].sort((a, b) => a.sort_order - b.sort_order);
  const nextSortOrder = sorted.length > 0 ? Math.max(...sorted.map((r) => r.sort_order)) + 1 : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-text-sub">上から順に評価し、最初に一致したルールの表示レベルが採用されます。一致するルールが無ければ「非表示」になります。</p>
        {canManage && <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyRuleDraft(infoClass))}>ルールを追加</Button>}
      </div>
      {sorted.length === 0 ? (
        <EmptyState message="この情報クラスのルールはまだありません" description="未設定の間、この情報は誰にも表示されません (fail-safe)。" />
      ) : (
        <ul className="space-y-2">
          {sorted.map((r, i) => (
            <li key={r.rule_id} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white p-3">
              <div className="min-w-0 flex items-center gap-2">
                {canManage && (
                  <div className="flex flex-col shrink-0">
                    <button type="button" aria-label="上へ" disabled={i === 0} onClick={() => move(r, -1)}
                      className="w-11 h-11 sm:w-8 sm:h-6 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronUp size={14} /></button>
                    <button type="button" aria-label="下へ" disabled={i === sorted.length - 1} onClick={() => move(r, 1)}
                      className="w-11 h-11 sm:w-8 sm:h-6 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronDown size={14} /></button>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text-body">
                    {r.node_id ? (nodeNameById.get(r.node_id) ?? r.node_id) : 'テナント全体'} ・ {setLabel(r.condition_set_id)}
                  </p>
                  <p className="text-xs text-text-sub mt-0.5">表示レベル: <span className="font-semibold text-text-body">{displayLevelLabel(infoClass, r.display_level)}</span>{r.label && ` ・ ${r.label}`}</p>
                </div>
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setDraft({
                    rule_id: r.rule_id, node_id: r.node_id, info_class: r.info_class, condition_set_id: r.condition_set_id,
                    display_level: r.display_level, label: r.label ?? '',
                  })}>編集</Button>
                  <button type="button" aria-label="削除" onClick={() => setDeleteTarget(r)}
                    className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {draft && (
        <RuleModal draft={draft} setDraft={setDraft} group={group} conditionSets={conditionSets} nextSortOrder={nextSortOrder}
          onClose={() => setDraft(null)} onSaved={() => { onChanged(); setDraft(null); }} />
      )}
      <ConfirmDialog open={!!deleteTarget} title="可視性ルールを削除" message="このルールを削除します。この操作は取り消せません。"
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.rule_id)} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// プレビュー: 「Aさんから見たBさんの表示」
// -----------------------------------------------------------------------------
function VisibilityPreview() {
  const [viewerId, setViewerId] = useState<string | null>(null);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [infoClass, setInfoClass] = useState<string>('compensation_amount');
  const [nodeId, setNodeId] = useState<string | null>(null);

  const previewMut = useMutation({
    mutationFn: () => apiFetch<{ display_level: string }>('/settings/visibility-rules/preview', {
      method: 'POST',
      body: JSON.stringify({ viewer_employee_id: viewerId, subject_employee_id: subjectId, info_class: infoClass, node_id: nodeId }),
    }),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'プレビューに失敗しました'),
  });

  const canPreview = !!subjectId && !!nodeId;

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 sm:p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Eye size={16} className="text-text-sub shrink-0" />
        <h3 className="text-sm font-semibold text-text-body">「誰から見た誰の表示」をプレビュー</h3>
      </div>
      <p className="text-xs text-text-sub">設定した条件が意図通りに機能するか、実際の社員を選んで確認できます。</p>
      <div className="flex flex-wrap gap-3">
        <EmployeeSelector employeeId={viewerId} onChange={setViewerId} label="閲覧者 (省略可)" allowNull nullLabel="(閲覧者を指定しない)" />
        <EmployeeSelector employeeId={subjectId} onChange={setSubjectId} label="対象者" />
        <div className="min-w-0 w-full sm:w-72">
          <label className="block text-xs text-text-sub mb-1">情報クラス</label>
          <Select value={infoClass} onValueChange={setInfoClass}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(INFO_CLASS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <NodeSelector nodeId={nodeId} onChange={setNodeId} label="評価対象ノード" />
      </div>
      <Button variant="secondary" size="sm" leftIcon={<Eye size={14} />} disabled={!canPreview || previewMut.isPending} loading={previewMut.isPending}
        onClick={() => previewMut.mutate()}>
        プレビューする
      </Button>
      {previewMut.data && (
        <p className="text-sm" aria-live="polite">
          結果: <span className="font-semibold text-text-body">{displayLevelLabel(infoClass, previewMut.data.display_level)}</span>
        </p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
export function VisibilityRulesSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const permsReady = usePermsReady();
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'tenant_config', 'manage');
  const [infoClass, setInfoClass] = useState<string>('compensation_amount');
  const group = resolveGroup(infoClass);
  const qc = useQueryClient();

  const { data: rules, isLoading } = useQuery<Rule[]>({
    queryKey: ['visibility-rules'],
    queryFn: () => apiFetch<Rule[]>('/settings/visibility-rules'),
  });
  const { data: catalog } = useQuery<ConditionCatalog>({ queryKey: ['condition-catalog'], queryFn: () => apiFetch('/condition-catalog'), staleTime: 5 * 60 * 1000 });
  // ⚠️ 段階⑧: 対象者条件の取得元 usage_point は選択中の情報クラスが属するグループで決まる
  // (compensation 系6件は 'visibility_rule' を共有、leave/family はそれぞれ専用の usage_point)。
  const { data: setsData } = useQuery<ConditionSetsResponse>({
    queryKey: ['condition-sets', group.usagePointId],
    queryFn: () => apiFetch(`/condition-sets?usage_point_id=${group.usagePointId}`),
  });
  const usagePoint = catalog?.usage_points.find((u) => u.usage_point_id === group.usagePointId);
  const attributeTypesById = useMemo(() => new Map((catalog?.attribute_types ?? []).map((t) => [t.attribute_type_id, t])), [catalog]);
  const conditionSets = useMemo(() => (setsData?.sets ?? []).map((s) => ({ set_id: s.set_id, label: s.label })), [setsData]);

  const [showConditionEditor, setShowConditionEditor] = useState(false);
  const onChanged = () => qc.invalidateQueries({ queryKey: ['visibility-rules'] });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">可視性ルール</h2>
        <p className="text-sm text-text-sub mt-1">
          給与額・年収残枠・評価スコア等の機微な情報を、閲覧者と対象者の関係 (所属・役職の上下・雇用形態等) に応じて表示範囲を制御します。
          一致するルールが無い場合は常に非表示 (fail-safe) になります。
        </p>
        {permsReady && !canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (編集にはテナント全体の管理権限が必要です)。</p>}
      </div>

      <VisibilityPreview />

      {usagePoint && (
        <div className="border border-surface-border rounded-lg bg-white">
          <button type="button" onClick={() => setShowConditionEditor((v) => !v)} aria-expanded={showConditionEditor}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 min-h-[44px] text-left text-sm font-semibold text-text-body">
            対象者条件を管理 (閲覧者との関係を含む)
          </button>
          {showConditionEditor && (
            <div className="px-3 pb-4">
              <ConditionSetEditor usagePointId={group.usagePointId} usagePoint={usagePoint} attributeTypesById={attributeTypesById} />
            </div>
          )}
        </div>
      )}

      <div className="min-w-0 w-full sm:w-96">
        <label className="block text-xs text-text-sub mb-1">情報クラス</label>
        <Select value={infoClass} onValueChange={setInfoClass}>
          <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(INFO_CLASS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <RulesListForInfoClass
          infoClass={infoClass}
          group={group}
          rules={(rules ?? []).filter((r) => r.info_class === infoClass)}
          canManage={canManage}
          conditionSets={conditionSets}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}
